import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import TopRightMenuAvatar from './TopRightMenu';
import BibleNoteDialog from './BibleNoteDialog';
import { bibleNotesRequest, noteReference } from './bibleNotesApi';
import { BIBLE_BOOK_ORDER, normalizeBookName } from '../utils/scriptureReferences';
import { useModal } from '../ModalContext';
import { ConvertSimplified, ConvertTraditional, TranslateLanguage } from './KeywordTranslator';
import './bibleNotes.css';
import './bibleNoteControls.css';

const bookPositions = new Map(BIBLE_BOOK_ORDER.map((book, index) => [book, index]));
const alternateScript = value => {
  const traditional = ConvertTraditional(value);
  return traditional === value ? ConvertSimplified(value) : traditional;
};
const sortNotes = (items, sort) => [...items].sort((a, b) => {
  if (sort === 'scripture') {
    const bookA = bookPositions.get(normalizeBookName(a.book) || normalizeBookName(a.bookName)) ?? 999;
    const bookB = bookPositions.get(normalizeBookName(b.book) || normalizeBookName(b.bookName)) ?? 999;
    return bookA - bookB
      || Number(a.chapter) - Number(b.chapter)
      || Number(a.startVerse) - Number(b.startVerse)
      || Number(a.endVerse) - Number(b.endVerse)
      || new Date(b.updatedAt) - new Date(a.updatedAt);
  }
  return new Date(a.updatedAt) - new Date(b.updatedAt) || Number(a.id) - Number(b.id);
});

async function loadAllNotes(search) {
  const first = await bibleNotesRequest(`?${new URLSearchParams({ ...search, page: '1', pageSize: '100' })}`);
  const items = [...(first.items || [])];
  const total = Number(first.total) || items.length;
  for (let page = 2; page <= Math.ceil(total / 100); page += 4) {
    const nextPages = Array.from({ length: Math.min(4, Math.ceil(total / 100) - page + 1) }, (_, index) => page + index);
    const results = await Promise.all(nextPages.map(number =>
      bibleNotesRequest(`?${new URLSearchParams({ ...search, page: String(number), pageSize: '100' })}`)));
    results.forEach(result => items.push(...(result.items || [])));
  }
  return { items, total };
}

function NoteScriptureExcerpt({ text, translate }) {
  const excerptRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);

  useEffect(() => {
    if (expanded || !excerptRef.current) return undefined;
    const excerpt = excerptRef.current;
    const measure = () => setCanExpand(excerpt.scrollHeight > excerpt.clientHeight + 1);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(excerpt);
    return () => observer.disconnect();
  }, [text, expanded]);

  return <div className="bible-notes-excerpt">
    <blockquote ref={excerptRef} className={expanded ? '' : 'is-collapsed'}>{translate(text)}</blockquote>
    {(canExpand || expanded) && <button type="button" className="bible-notes-excerpt-toggle" aria-expanded={expanded} onClick={() => setExpanded(value => !value)}>
      {translate(expanded ? '收起經文' : '展開經文')}
    </button>}
  </div>;
}

export default function BibleNotesPage() {
  const navigate = useNavigate();
  const { setLoginModalVisible } = useModal();
  const language = localStorage.userLanguage || ((navigator.language || navigator.userLanguage) === 'zh-CN' ? 'S' : 'T');
  const translate = text => TranslateLanguage(language, text);
  const [params] = useSearchParams();
  const initialBook = params.get('book') || '';
  const initialCanonicalBook = normalizeBookName(initialBook);
  const [query, setQuery] = useState('');
  const [book, setBook] = useState(initialBook);
  const [chapter, setChapter] = useState(params.get('chapter') || '');
  const [filters, setFilters] = useState({ query: '', book: initialCanonicalBook || initialBook,
    bookAlt: alternateScript(initialCanonicalBook || initialBook), chapter: params.get('chapter') || '' });
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState('recent');
  const [refreshKey, setRefreshKey] = useState(0);
  const [data, setData] = useState({ items: [], total: 0 });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const sortedNotesCache = useRef(null);

  useEffect(() => {
    if (openMenuId === null) return undefined;
    const closeOutside = event => {
      if (!event.target.closest('.bible-notes-card-menu')) setOpenMenuId(null);
    };
    const closeEscape = event => { if (event.key === 'Escape') setOpenMenuId(null); };
    document.addEventListener('pointerdown', closeOutside);
    document.addEventListener('keydown', closeEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOutside);
      document.removeEventListener('keydown', closeEscape);
    };
  }, [openMenuId]);

  useEffect(() => {
    if (!sessionStorage.getItem('token')) return;
    let cancelled = false;
    const search = Object.fromEntries(Object.entries(filters).filter(([, value]) => value));
    const cacheKey = JSON.stringify({ search, sort, refreshKey });
    if (sort !== 'recent' && sortedNotesCache.current?.key === cacheKey) {
      const { items, total } = sortedNotesCache.current;
      setData({ items: items.slice((page - 1) * 20, page * 20), total });
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const request = sort === 'recent'
      ? bibleNotesRequest(`?${new URLSearchParams({ ...search, page: String(page), pageSize: '20' })}`)
      : loadAllNotes(search).then(result => {
        const sorted = sortNotes(result.items, sort);
        if (!cancelled) sortedNotesCache.current = { key: cacheKey, items: sorted, total: result.total };
        return { total: result.total, items: sorted.slice((page - 1) * 20, page * 20) };
      });
    request
      .then(result => { if (!cancelled) { setData(result); setError(''); } })
      .catch(e => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [filters, page, sort, refreshKey]);

  const search = event => {
    event.preventDefault();
    setPage(1);
    const term = query.trim();
    const bookTerm = book.trim();
    const canonicalBook = normalizeBookName(bookTerm);
    setFilters({
      query: term,
      queryAlt: term ? alternateScript(term) : '',
      book: canonicalBook || bookTerm,
      bookAlt: canonicalBook ? alternateScript(canonicalBook) : alternateScript(bookTerm),
      chapter: chapter.trim(),
    });
  };
  const remove = async note => {
    if (!window.confirm(translate(`刪除 ${noteReference(note)} 的這篇筆記？`))) return;
    try {
      await bibleNotesRequest(`/${note.id}`, { method: 'DELETE' });
      setOpenMenuId(null);
      if (page > 1 && data.items.length === 1) setPage(page - 1);
      else setRefreshKey(current => current + 1);
    } catch (e) { setError(e.message); }
  };

  return <>
    <header className="bible-notes-page-head bible-notes-page"><button onClick={() => navigate(-1)}>{translate('返回')}</button><TopRightMenuAvatar /></header>
    <main className="bible-notes-page">
      <div className="bible-notes-page-head">
        <div className="bible-notes-title-group">
          <h1>{translate('我的聖經筆記')}</h1>
          <div className="bible-notes-info">
            <button type="button" className="bible-notes-info-trigger" aria-label={translate('關於我的聖經筆記')} aria-describedby="bible-notes-info-tip">ⓘ</button>
            <span id="bible-notes-info-tip" className="bible-notes-info-popover" role="tooltip">{translate('筆記只屬於你的帳號，不會顯示在讀經計劃的共享筆記中。')}</span>
          </div>
        </div>
        <span>{data.total} {translate('篇')}</span>
      </div>
      {!sessionStorage.getItem('token') ? <p>{translate('請先登入以查看個人筆記。')} <button onClick={() => setLoginModalVisible(true)}>{translate('登入')}</button></p> : <>
        <label className="bible-notes-sort">{translate('排序')}
          <select value={sort} onChange={event => { setSort(event.target.value); setPage(1); }}>
            <option value="recent">{translate('最近更新')}</option>
            <option value="oldest">{translate('最早更新')}</option>
            <option value="scripture">{translate('聖經經卷順序')}</option>
          </select>
        </label>
        <form className="bible-notes-search" onSubmit={search}>
          <input aria-label={translate('搜尋筆記')} placeholder={translate('搜尋標題、內容、經文或標籤')} value={query} onChange={e => setQuery(e.target.value)} />
          <input aria-label={translate('書卷簡稱')} placeholder={translate('書卷（如：創）')} value={book} onChange={e => setBook(e.target.value)} />
          <input aria-label={translate('章數')} type="number" min="1" placeholder={translate('章')} value={chapter} onChange={e => setChapter(e.target.value)} />
          <button className="primary" type="submit">{translate('搜尋')}</button>
        </form>
        {error && <p className="bible-note-error" role="alert">{translate(error)}</p>}
        {loading ? <p>{translate('載入中…')}</p> : !data.items?.length ? <p>{translate('沒有找到筆記。閱讀經文時點擊「筆記」即可開始記錄。')}</p> : data.items.map(note => <article className="bible-notes-card" key={note.id}>
          <div className="bible-notes-card-menu">
            <button type="button" className="bible-notes-menu-trigger" aria-label={translate(`${noteReference(note)} 更多操作`)} aria-haspopup="menu" aria-expanded={openMenuId === note.id} onClick={() => setOpenMenuId(current => current === note.id ? null : note.id)}>⋮</button>
            {openMenuId === note.id && <div className="bible-notes-menu-items" role="menu">
              <button type="button" role="menuitem" onClick={() => { setOpenMenuId(null); setSelected(note); }}>{translate('編輯')}</button>
              <button type="button" role="menuitem" onClick={() => remove(note)}>{translate('刪除')}</button>
            </div>}
          </div>
          <small>{translate(noteReference(note))} · {translate('更新於')} {new Date(note.updatedAt).toLocaleString(language === 'S' ? 'zh-CN' : 'zh-TW')}</small>
          {note.title && <h2>{note.title}</h2>}
          {note.scriptureText && <NoteScriptureExcerpt text={note.scriptureText} translate={translate} />}
          <p>{note.content}</p>
          {note.tags && <small>#{note.tags}</small>}
        </article>)}
        {data.total > 20 && <div className="bible-notes-pagination"><button disabled={page === 1} onClick={() => setPage(page - 1)}>{translate('上一頁')}</button><span>{page} / {Math.ceil(data.total / 20)}</span><button disabled={page * 20 >= data.total} onClick={() => setPage(page + 1)}>{translate('下一頁')}</button></div>}
      </>}
    </main>
    {selected && <BibleNoteDialog key={selected.id} book={selected.book} bookName={selected.bookName} chapter={selected.chapter} startVerse={selected.startVerse} endVerse={selected.endVerse} initialNote={selected} onClose={() => { setSelected(null); setRefreshKey(current => current + 1); }} />}
  </>;
}
